import { useRef, useState } from 'react'
import * as styles from './styles'

const LABEL_MAX_LENGTH = 24

type VersionLabelEditorProps = {
  initialValue: string
  onCommit: (label: string | null) => void
  onCancel: () => void
}

/**
 * Inline text field for naming a version's label, shared by the rail row and the
 * preview byline. Commits on blur/Enter and discards on Escape; an empty value
 * commits `null` to clear the label.
 */
export const VersionLabelEditor: React.VFC<VersionLabelEditorProps> = ({
  initialValue,
  onCommit,
  onCancel,
}) => {
  const [value, setValue] = useState(initialValue)
  // Escape unmounts the input, which also fires `blur`; this guards blur from
  // committing the discarded value.
  const cancelledRef = useRef(false)

  const commit = () => {
    if (cancelledRef.current) return
    const trimmed = value.trim()
    onCommit(trimmed.length > 0 ? trimmed : null)
  }

  return (
    <input
      // eslint-disable-next-line jsx-a11y/no-autofocus -- the editor only exists while it is being typed into: it is opened by an explicit action and commits on blur
      autoFocus
      aria-label="Version label"
      placeholder="Add label"
      css={styles.labelInput}
      maxLength={LABEL_MAX_LENGTH}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      onBlur={commit}
      onKeyDown={(event) => {
        event.stopPropagation()
        if (event.key === 'Enter') {
          event.currentTarget.blur()
        } else if (event.key === 'Escape') {
          cancelledRef.current = true
          onCancel()
        }
      }}
    />
  )
}
